/// <amd-module name="Mention/MarkdownWithAutocomplete/Components/MarkdownPreviewWithAutocomplete" />
import { IAutocompleteProvider } from "Mention/Autocomplete/Contracts";
import { getAutocompleteProviders } from "Mention/AutocompleteResolver/AutocompleteContributionResolverService";
import { parseCodeBlocksFromText, parseContainersFromText } from "Mention/Common/Util";
import * as React from "react";
import { IMarkdownTransformContext, MarkdownRendererOptions } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IMarkdownPreviewProps, MarkdownPreview } from "VSS/Features/MarkdownEditor/Components";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature"

export function MarkdownPreviewWithAutocomplete(props: IMarkdownPreviewProps) {
    return (
        <WrappedComponent
            wrappedType={"markdown-with-autocomplete-component"}
            wrappedProps={props}
            dependencies={["ms.vss-mention.mention-autocomplete"]}
        />
    );
}

interface ArtifactIndexResult {
    start: number;
    end: number;
}

export class MarkdownPreviewWithAutocompleteComponent extends VssComponent<IMarkdownPreviewProps> {
    public render(): JSX.Element {
        return (
            <Observer rawContent={this.props.value} providers={getAutocompleteProviders(this.context.pageContext)}>
                {(props: { rawContent?: string; providers: IAutocompleteProvider[] }) => {
                    const { rawContent, providers } = props;
                    const options: MarkdownRendererOptions = { ...this.props.options };

                    if (rawContent) {
                        options.transformers = options.transformers ? [...options.transformers] : [];
                        options.transformers.push((transformContext: IMarkdownTransformContext): void => {
                            if (isFeatureFlagEnabled(this.context.pageContext, "VisualStudio.Mentions.ExcludeMentionsFromCodeBlocksFix")) {
                                providers.forEach((provider) => {
                                    if (provider.transformMarkdown) {
                                        provider.transformMarkdown(transformContext, this.getIgnoreMatchNew(transformContext));
                                    }
                                });
                            } else {
                                const ignoreBlocks = [...parseCodeBlocksFromText(transformContext.markup), ...parseContainersFromText(transformContext.markup)]
                                providers.forEach((provider) => {
                                    if (provider.transformMarkdown) {
                                        provider.transformMarkdown(transformContext, this.getIgnoreMatch(ignoreBlocks));
                                    }
                                });
                            }
                        });
                    }

                    return <MarkdownPreview {...this.props} options={options} value={rawContent} />;
                }}
            </Observer>
        );
    }

    private getIgnoreMatchNew(transformContext: IMarkdownTransformContext): ((matchIndex: number) => boolean) | undefined {
        return (matchIndex: number) => {
            const ignoreBlocks = [...parseCodeBlocksFromText(transformContext.markup), ...parseContainersFromText(transformContext.markup)]
            return ignoreBlocks.some((ignoreBlock) => matchIndex >= ignoreBlock.start && matchIndex <= ignoreBlock.end);
        };
    }

    private getIgnoreMatch(ignoreBlocks: ArtifactIndexResult[]): ((matchIndex: number) => boolean) | undefined {
        if (ignoreBlocks.length) {
            return (matchIndex: number) => {
                return ignoreBlocks.some((ignoreBlock) => matchIndex >= ignoreBlock.start && matchIndex <= ignoreBlock.end);
            };
        }
    }
}

VssComponent.register("markdown-with-autocomplete-component", MarkdownPreviewWithAutocompleteComponent);
